// app/src/dashboard/widgets/EarningsCalendarWidget.tsx
import { useState, useEffect } from 'react'
import { useQuery } from 'wasp/client/operations'
import { getEarningsCalendar } from 'wasp/client/operations'
import { useTranslations } from '../../client/i18n/useTranslations'
import { Card } from '../../client/components/ui/card'
import { CardSectionHeader } from '../../client/components/ui/card-section-header'
import { format, addDays, subDays } from 'date-fns'
import { ChevronLeft, ChevronRight, Settings2, Search } from 'lucide-react'

type EarningsEvent = {
  id: string
  earningsDate: string | Date
  symbol: string
  companyName: string
  exchange: string
  timing: string
  epsEstimate: number | null
  epsActual: number | null
  epsSurprise: number | null
  revenueEstimate: number | null
  revenueActual: number | null
  revenueSurprise: number | null
  priceReaction: number | null
  guidance: string | null
}

type ColumnKey =
  | 'symbol' | 'company' | 'timing' | 'epsEstimate' | 'epsActual' | 'epsSurprise'
  | 'revenueEstimate' | 'revenueActual' | 'revenueSurprise' | 'priceReaction' | 'guidance'

const DEFAULT_COLUMNS: ColumnKey[] = ['symbol', 'company', 'timing', 'epsActual', 'epsSurprise', 'revenueSurprise']
const storageKey = 'earnings-widget-cols'

function SurpriseValue({ value }: { value: number | null }) {
  if (value == null) return <span className="text-muted-foreground/40">—</span>
  const color = value > 0 ? 'text-primary' : value < 0 ? 'text-destructive' : 'text-muted-foreground'
  return (
    <span className={`font-mono font-medium ${color}`}>
      {value > 0 ? '+' : ''}{value.toFixed(1)}%
    </span>
  )
}

function TimingBadge({ timing, t }: { timing: string; t: ReturnType<typeof useTranslations> }) {
  const config: Record<string, { label: string; className: string }> = {
    bmo: { label: t.marketDataWidgets.earningsCalendar.timingBmo, className: 'bg-blue-500/20 text-blue-400' },
    amc: { label: t.marketDataWidgets.earningsCalendar.timingAmc, className: 'bg-purple-500/20 text-purple-400' },
    dmh: { label: t.marketDataWidgets.earningsCalendar.timingDmh, className: 'bg-muted text-muted-foreground' },
  }
  const c = config[timing] ?? config.dmh
  return (
    <span className={`text-[9px] rounded px-1.5 py-0.5 font-medium whitespace-nowrap ${c.className}`}>
      {c.label}
    </span>
  )
}

export function EarningsCalendarWidget() {
  const t = useTranslations()
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [search, setSearch] = useState('')
  const [showConfig, setShowConfig] = useState(false)

  const [visibleColumns, setVisibleColumns] = useState<ColumnKey[]>(() => {
    try {
      const saved = localStorage.getItem(storageKey)
      return saved ? JSON.parse(saved) : DEFAULT_COLUMNS
    } catch {
      return DEFAULT_COLUMNS
    }
  })

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(visibleColumns)) } catch {}
  }, [visibleColumns])

  const { data: events, isLoading } = useQuery(getEarningsCalendar, {
    date: selectedDate.toISOString(),
  })

  const ALL_COLUMNS: { key: ColumnKey; label: string }[] = [
    { key: 'symbol', label: t.marketDataWidgets.earningsCalendar.colSymbol },
    { key: 'company', label: t.marketDataWidgets.earningsCalendar.colCompany },
    { key: 'timing', label: t.marketDataWidgets.earningsCalendar.colTiming },
    { key: 'epsEstimate', label: t.marketDataWidgets.earningsCalendar.colEpsEstimate },
    { key: 'epsActual', label: t.marketDataWidgets.earningsCalendar.colEpsActual },
    { key: 'epsSurprise', label: t.marketDataWidgets.earningsCalendar.colEpsSurprise },
    { key: 'revenueEstimate', label: t.marketDataWidgets.earningsCalendar.colRevenueEstimate },
    { key: 'revenueActual', label: t.marketDataWidgets.earningsCalendar.colRevenueActual },
    { key: 'revenueSurprise', label: t.marketDataWidgets.earningsCalendar.colRevenueSurprise },
    { key: 'priceReaction', label: t.marketDataWidgets.earningsCalendar.colPriceReaction },
    { key: 'guidance', label: t.marketDataWidgets.earningsCalendar.colGuidance },
  ]

  const filtered = (events as EarningsEvent[] ?? []).filter((e) =>
    search === '' ||
    e.symbol.toLowerCase().includes(search.toLowerCase()) ||
    e.companyName.toLowerCase().includes(search.toLowerCase())
  )

  function renderCell(event: EarningsEvent, col: ColumnKey): React.ReactNode {
    switch (col) {
      case 'symbol':
        return <span className="font-mono font-bold text-primary">{event.symbol}</span>
      case 'company':
        return <span className="whitespace-nowrap">{event.companyName}</span>
      case 'timing':
        return <TimingBadge timing={event.timing} t={t} />
      case 'epsEstimate':
        return <span className="font-mono text-muted-foreground">{event.epsEstimate?.toFixed(2) ?? '—'}</span>
      case 'epsActual':
        return <span className="font-mono">{event.epsActual?.toFixed(2) ?? '—'}</span>
      case 'epsSurprise':
        return <SurpriseValue value={event.epsSurprise} />
      case 'revenueEstimate':
        return (
          <span className="font-mono text-muted-foreground">
            {event.revenueEstimate != null ? `${(event.revenueEstimate / 1e9).toFixed(1)}B` : '—'}
          </span>
        )
      case 'revenueActual':
        return (
          <span className="font-mono">
            {event.revenueActual != null ? `${(event.revenueActual / 1e9).toFixed(1)}B` : '—'}
          </span>
        )
      case 'revenueSurprise':
        return <SurpriseValue value={event.revenueSurprise} />
      case 'priceReaction':
        return <SurpriseValue value={event.priceReaction} />
      case 'guidance': {
        if (!event.guidance) return <span className="text-muted-foreground/40">—</span>
        const map: Record<string, { label: string; color: string }> = {
          raised: { label: t.marketDataWidgets.earningsCalendar.guidanceRaised, color: 'text-primary' },
          lowered: { label: t.marketDataWidgets.earningsCalendar.guidanceLowered, color: 'text-destructive' },
          maintained: { label: t.marketDataWidgets.earningsCalendar.guidanceMaintained, color: 'text-muted-foreground' },
        }
        const g = map[event.guidance] ?? { label: event.guidance, color: '' }
        return <span className={`text-[9px] ${g.color}`}>{g.label}</span>
      }
    }
  }

  return (
    <Card className="flex flex-col h-full overflow-hidden">
      <CardSectionHeader
        label={t.marketDataWidgets.earningsCalendar.title}
        isDark={false}
        compact
        rightContent={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <button onClick={() => setSelectedDate(subDays(selectedDate, 1))} className="p-0.5 hover:text-primary">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-muted-foreground w-20 text-center">
                {format(selectedDate, 'dd/MM/yy')}
              </span>
              <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-0.5 hover:text-primary">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex items-center gap-1">
              <div className="relative">
                <Search className="w-3 h-3 absolute left-1.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="AAPL..."
                  className="text-xs bg-muted rounded pl-5 pr-2 py-0.5 w-16 focus:w-24 transition-all outline-none"
                />
              </div>
              <button onClick={() => setShowConfig(!showConfig)} className="p-1 hover:text-primary">
                <Settings2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        }
      />

      {showConfig && (
        <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-border bg-accent/20 shrink-0">
          {ALL_COLUMNS.map((col) => (
            <label key={col.key} className="flex items-center gap-1 text-[10px] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={visibleColumns.includes(col.key)}
                onChange={(e) =>
                  setVisibleColumns((prev) =>
                    e.target.checked ? [...prev, col.key] : prev.filter((k) => k !== col.key)
                  )
                }
                className="w-2.5 h-2.5 accent-primary"
              />
              {col.label}
            </label>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex flex-col gap-1 p-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse h-8 rounded bg-muted" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm p-4">
            {t.marketDataWidgets.earningsCalendar.noEarnings}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-card border-b border-border">
              <tr>
                {ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).map((col) => (
                  <th key={col.key} className="px-2 py-1 text-left text-muted-foreground font-normal whitespace-nowrap">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((event) => (
                <tr
                  key={event.id}
                  className={`border-b border-border/40 hover:bg-accent/30 ${!event.epsActual ? 'opacity-60' : ''}`}
                >
                  {ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key)).map((col) => (
                    <td key={col.key} className="px-2 py-1.5">
                      {renderCell(event, col.key)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  )
}
